'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  buildPostLoginRedirect,
  normalizeExplicitPostLoginRedirect,
  requiresHardNavigation
} from '@/lib/auth/redirects';
import { login, maybeBridgeLegacyFromLocalStorage } from '@/lib/auth/client';
import type { SessionResponse } from '@/lib/auth/types';
import { useAuth } from '@/lib/auth/AuthProvider';
import { isProvider } from '@/lib/auth/roles';
import { flagMessagesOpenOnLogin } from '@/features/messages/openOnLogin';
import { isValidEmail } from './authLoginForm.helpers';

type UseAuthLoginFormOptions = {
  redirect?: string | null;
  initialEmail?: string;
};

type AuthLoginFormState = {
  email: string;
  password: string;
  submitting: boolean;
  error: string | null;
  canSubmit: boolean;
  emailError: boolean;
  emailErrorText: string | null;
};

type AuthLoginFormHandlers = {
  handleEmailChange: (value: string) => void;
  handlePasswordChange: (value: string) => void;
  handleSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
};

export const useAuthLoginForm = (
  options: UseAuthLoginFormOptions = {}
): AuthLoginFormState & AuthLoginFormHandlers => {
  const { redirect, initialEmail } = options;
  const router = useRouter();
  const { setSession } = useAuth();
  const [email, setEmail] = useState(initialEmail ?? '');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void maybeBridgeLegacyFromLocalStorage();
  }, []);

  const canSubmit = useMemo(
    () => Boolean(email && password && isValidEmail(email)),
    [email, password]
  );

  const emailError = useMemo(
    () => Boolean(email && !isValidEmail(email)),
    [email]
  );
  const emailErrorText = emailError
    ? 'Please enter a valid email address'
    : null;

  const navigateTo = (destination: string) => {
    if (requiresHardNavigation(destination)) {
      window.location.assign(destination);
      return;
    }
    router.replace(destination);
  };

  const handleSuccess = async (session: SessionResponse) => {
    const normalizedRedirect = normalizeExplicitPostLoginRedirect(redirect);
    setSession(session);

    // Breeders land on their dashboard, so the messages modal must not open
    // over it; buyers keep the straight-to-conversations behaviour.
    if (!isProvider(session)) {
      flagMessagesOpenOnLogin();
    }

    navigateTo(buildPostLoginRedirect(session, normalizedRedirect));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);

    try {
      const session = await login(email, password);
      await handleSuccess(session);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEmailChange = (value: string) => setEmail(value);
  const handlePasswordChange = (value: string) => setPassword(value);

  return {
    email,
    password,
    submitting,
    error,
    canSubmit,
    emailError,
    emailErrorText,
    handleEmailChange,
    handlePasswordChange,
    handleSubmit
  };
};
